import { HighlightOff, PlaylistAddCheck } from "@mui/icons-material";
import { Badge } from "@mui/material";
import { useMotionValue } from "motion/react";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { PIC, SVG_MUI } from "@ryde/assets";
import {
  AccessControl,
  FloatingMenuButton,
  FloatingMenuStyled,
  FloatingMenuTooltipWrapper,
  FloatingMenuTooltipWrapperIfDisabled,
  MuiButton,
  useFloatMenu,
} from "@ryde/controls";
import {
  useAiAdvisor,
  useHubRideActionConfirm,
  useModalConfirm,
  useModalConfirmsRide,
  usePageContext,
  useRidesBulkActions,
  useSelectedIds,
} from "@ryde/redux";
import {
  bulkOperationsDrawerAF,
  ConfirmResult,
  HubRideActionKind,
  isSelectedTableRowsIdsExpandedSF,
  Log,
  RideDeleteActionsResultEnum,
  RideStatusEnum,
  selectedRidesSelector,
  selectedTableRowsIdsAtomFamily,
  useGridExpandedCollapse,
  useIsMobile,
  useSystemParams,
} from "@ryde/shared";
import { greyMui } from "@ryde/theme";

import { RidesEditStatusDropdown } from "./parts";
import { ExpandCollapseIconStyled } from "./rides.floating.menu.styled";

const MAX_EXPANDED_ROWS = 10;
const MAX_RIDES_AI_ADVISOR_ROWS = 3;

export const RidesFloatingMenu: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { isMobile } = useIsMobile();
  const { enableAIFeatures } = useSystemParams();
  const setBulkOperationsDrawerState = useSetRecoilState(
    bulkOperationsDrawerAF(entity),
  );
  const { confirmDialogResult } = useModalConfirm();

  const { bulkArchive, bulkRebuildRoutes, bulkStarRides, bulkSendToSupplier } =
    useRidesBulkActions();

  const selectedRideIds = useRecoilValue(
    selectedTableRowsIdsAtomFamily(entity),
  );
  const selectedRides = useRecoilValue(selectedRidesSelector);

  const { clearSelectedIds } = useSelectedIds();
  const isOpened = !!selectedRideIds?.length;
  const {
    setBottomY,
    bottomY,
    isInitFinished,
    leftX,
    setLeftX,
    floatingMenuRef,
  } = useFloatMenu({ isOpened, page: "rides" });

  const left = useMotionValue(leftX);
  const bottom = useMotionValue(bottomY);
  const { confirmRideArchiveAction } = useModalConfirmsRide();
  const { confirmSelectedHubRidesAction } = useHubRideActionConfirm();
  const { openBulkAIAdvisorDrawer } = useAiAdvisor(entity);

  const { expandByIds, collapseByIds } = useGridExpandedCollapse();
  const isSelectedTableRowsIdsExpanded = useRecoilValue(
    isSelectedTableRowsIdsExpandedSF(entity),
  );

  const handleStarClick = useEvent(() => {
    bulkStarRides(selectedRideIds);
  });

  const handleArchiveClick = useEvent(async () => {
    // Hub paired-ride warning first (same order as single-ride drawer), then booking-status dialog.
    const isHubArchiveConfirmed = await confirmSelectedHubRidesAction({
      selectedRideIds,
      rides: selectedRides,
      actionKind: HubRideActionKind.Archive,
    });

    if (!isHubArchiveConfirmed) {
      Log.warnAndToast(
        t(`general.RIDES_ACTIONS_WARNINGS.BULK_ARCHIVE_CANCELED`),
        selectedRideIds,
      );
      return;
    }

    const isEverySelectedRideCanceled = selectedRides.every(
      ride => ride.status === RideStatusEnum.Canceled,
    );
    let archiveRidesAction: RideDeleteActionsResultEnum;

    if (isEverySelectedRideCanceled) {
      // Already-canceled rides: skip booking-status choice; preserve booking status on archive.
      const confirmationResult = await confirmDialogResult({
        message: t(`general.ARCHIVE_ITEMS_MESSAGE`),
      });

      if (confirmationResult !== ConfirmResult.OK) {
        archiveRidesAction = RideDeleteActionsResultEnum.NoAction;
      } else {
        archiveRidesAction = RideDeleteActionsResultEnum.NotChangeBookingStatus;
      }
    } else {
      archiveRidesAction = await confirmRideArchiveAction();
    }

    if (archiveRidesAction === RideDeleteActionsResultEnum.NoAction) {
      Log.warnAndToast(
        t(`general.RIDES_ACTIONS_WARNINGS.BULK_ARCHIVE_CANCELED`),
        selectedRideIds,
      );

      return;
    }

    bulkArchive(selectedRideIds, archiveRidesAction);
  });

  const handleRebuildRouteClick = useEvent(async () => {
    const isHubModifyConfirmed = await confirmSelectedHubRidesAction({
      selectedRideIds,
      rides: selectedRides,
      actionKind: HubRideActionKind.Modify,
    });

    if (!isHubModifyConfirmed) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.REBUILD_ROUTES_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      const result = await bulkRebuildRoutes(selectedRideIds);
      if (!result?.success) {
        Log.warnAndToast(
          t(`general.RIDES_ACTIONS_WARNINGS.BULK_REBUILD_ROUTES_CANCELED`),
          selectedRideIds,
        );
      }
    }
  });

  const handleSendToSupplierClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.SEND_TO_SUPPLIER_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      bulkSendToSupplier(selectedRideIds);
    }
  });

  const onDragEndHandler = useEvent((): void => {
    setLeftX(left.get());
    setBottomY(bottom.get());
  });

  const handleExpandCollapseClick = (): void => {
    const idsSet = new Set(selectedRideIds);

    if (isSelectedTableRowsIdsExpanded) {
      collapseByIds(idsSet);
    } else {
      expandByIds(idsSet);
    }
  };

  const handleAnalyzeClick = (): void => {};

  useEffect(() => {
    if (!isOpened) {
      return;
    }

    left.set(leftX);
  }, [isOpened, left, leftX]);

  const isExpandButtonDisabled = selectedRideIds.length > MAX_EXPANDED_ROWS;
  const isRidesAIAdvisorButtonDisabled =
    selectedRideIds.length > MAX_RIDES_AI_ADVISOR_ROWS;

  return (
    isOpened && (
      <FloatingMenuStyled
        ref={floatingMenuRef}
        drag
        dragElastic={0}
        dragMomentum={false}
        onDragEnd={onDragEndHandler}
        style={{ x: left, bottom, opacity: !isInitFinished ? 0 : "initial" }}
      >
        {!isMobile && <SVG_MUI.GripVertical3SVG className="pointer grip" />}

        <Badge color="secondary" badgeContent={selectedRideIds?.length}>
          <PlaylistAddCheck style={{ color: greyMui._700 }} />
        </Badge>

        <FloatingMenuTooltipWrapper tooltip={t("ride.bulk.clear")}>
          <FloatingMenuButton onClick={clearSelectedIds}>
            <HighlightOff sx={{ color: greyMui._700 }} />
          </FloatingMenuButton>
        </FloatingMenuTooltipWrapper>

        {!isMobile && (
          <>
            <FloatingMenuTooltipWrapper tooltip={t("ride.bulk.star")}>
              <FloatingMenuButton onClick={handleStarClick}>
                <SVG_MUI.StarSVG color={greyMui.A700} />
                <span className="grey-text">{t(`general.STAR`)}</span>
              </FloatingMenuButton>
            </FloatingMenuTooltipWrapper>

            <AccessControl entity="ride" action="rebuild">
              <FloatingMenuTooltipWrapper tooltip={t("general.REBUILD_ROUTES")}>
                <FloatingMenuButton onClick={handleRebuildRouteClick}>
                  <SVG_MUI.ArrowsRotate3SVG color={greyMui.A700} />
                  <span className="grey-text">{t(`general.REBUILD`)}</span>
                </FloatingMenuButton>
              </FloatingMenuTooltipWrapper>
            </AccessControl>

            <AccessControl entity="ride" action="send_to_supplier">
              <FloatingMenuTooltipWrapper
                tooltip={t("general.SEND_TO_SUPPLIER")}
              >
                <FloatingMenuButton onClick={handleSendToSupplierClick}>
                  <SVG_MUI.SendSVG color={greyMui.A700} />
                  <span className="grey-text">{t(`general.SEND`)}</span>
                </FloatingMenuButton>
              </FloatingMenuTooltipWrapper>
            </AccessControl>

            <FloatingMenuTooltipWrapperIfDisabled
              tooltip={t("general.EXPAND_COLLAPSE_DISABLED_TOOLTIP")}
              disabled={isExpandButtonDisabled}
            >
              <FloatingMenuButton
                disabled={isExpandButtonDisabled}
                onClick={handleExpandCollapseClick}
              >
                <ExpandCollapseIconStyled
                  $isExpanded={isSelectedTableRowsIdsExpanded}
                />
                <span className="grey-text">
                  {t("general.EXPAND_COLLAPSE")}
                </span>
              </FloatingMenuButton>
            </FloatingMenuTooltipWrapperIfDisabled>

            {enableAIFeatures && (
              <FloatingMenuTooltipWrapperIfDisabled
                tooltip={t("aiAdvisor.rideAiAdvisorRowsLimitedTooltip")}
                disabled={isRidesAIAdvisorButtonDisabled}
              >
                <FloatingMenuButton
                  disabled={isRidesAIAdvisorButtonDisabled}
                  onClick={openBulkAIAdvisorDrawer}
                >
                  <PIC.ErlenmeyerFlaskSVG />
                  <span className="grey-text">{t("aiAdvisor.analyze")}</span>
                </FloatingMenuButton>
              </FloatingMenuTooltipWrapperIfDisabled>
            )}

            <RidesEditStatusDropdown />
            <AccessControl entity="ride" action="delete">
              <FloatingMenuTooltipWrapper tooltip={t("ride.bulk.archive")}>
                <FloatingMenuButton
                  className="archive"
                  onClick={handleArchiveClick}
                >
                  <SVG_MUI.ArchiveSVG />
                  <span>{t(`general.ARCHIVE`)}</span>
                </FloatingMenuButton>
              </FloatingMenuTooltipWrapper>
            </AccessControl>
          </>
        )}
        <MuiButton
          muiStyle="secondary"
          className="openDrawer"
          muiSize="small"
          onClick={() => setBulkOperationsDrawerState(true)}
        >
          <SVG_MUI.EllipsisVertical3SVG color="white" />
        </MuiButton>
      </FloatingMenuStyled>
    )
  );
};
